import React, {useState, useEffect} from "react";
import { IExtendedL3 } from "../../services/NetworkApi";
import { Table } from "../../UI/Table";
import { TableHeader } from "../TableHeader";
import { Typography } from "../../UI/Typography";
import { COLOR_WHITE } from "../../constants/styles";
import { TableHead } from "../../UI/TableHead";
import { TableRow } from "../../UI/TableRow";
import { TableCell } from "../../UI/TableCell";
import { HelpTooltip } from "../HelpTooltip";
import { EnhancedTableBody } from "../EnhancedTableBody";
import { StatusSwitch } from "./StatusSwitch";
import { SetAllToButtons } from "./SetAllToButtons";
import { VisibilitySwitch } from "./VisibilitySwitch";
import { TablePagination } from "../TablePagination";
import { useTablePaginationContext } from "../TablePagination/context";
import { usePaginationHandler } from "../../hooks/usePaginationHandler";
import { useMatchingRulesHandler } from "../../hooks/useMatchingRulesHandler";

const TOOLTIP_HELP_TITLE = "Renewal to Renewal Matching";
const TOOLTIP_HELP_CONTENT =
  "This tool restricts renewing residents from seeing or matching with other renewals. It is meant to prevent you from receiving matches that result in renewal transfers. We recommend disabling renewal matching. If you enable or disable renewal matching for a unit type, it will only affect future users - RoomSync will not break up any existing matches. Even if the renewal matching is off, you can bypass this and match renewals together using the admin matching tools.";

const TOOLTIP_VISIBLE_HELP_TITLE = "Renewals Visible";
const TOOLTIP_VISIBLE_HELP_CONTENT =
  "If visible, this means that renewals can see other renewals in RoomSync.";

interface IProps {
}

export const RenewalToRenewalMatchingTable: React.FC<IProps> = (props) => {
  const { data, isLoading, setData, getData } = useMatchingRulesHandler();
  const { page, setPage, perPage, setPages } = useTablePaginationContext();
  const [ filteredData, setFilteredData ] = useState<IExtendedL3[]>([]);
  const [ showArchived, setShowArchived ] = useState<boolean>(false);
  const { selectedRows, pages } = usePaginationHandler(
    filteredData,
    page,
    perPage
  );

  useEffect(() => {
    const newData = data.filter(row => showArchived? row : row.matchingRoundStatus!== "ARCHIVED");
    setFilteredData(newData);
  }, [showArchived, data]);

  const handleToggleArchiveChange = (_: any, checked: boolean) =>setShowArchived(checked);

  React.useEffect(() => {
    setPages(pages);
    setPage(1);
  }, [pages.length]);

  const isReadOnly = (row: any) => (row?.matchingRoundStatus === "ARCHIVED" || row?.matchingRoundStatus === "CLOSED");

  return (
    <>
      <TableHeader
        endAdornment={
          <SetAllToButtons
            unitTypes={data}
            updateData={setData}
            getData={getData}
            showArchived={showArchived}
            changeArchivedVisiblity={handleToggleArchiveChange}
          />
        }
      >
        <Typography fontWeight={600} color={COLOR_WHITE}>
          Renewal to Renewal Matching
        </Typography>
      </TableHeader>
      <Table>
        <TableHead secondary>
          <TableRow>
            <TableCell>Matching Round </TableCell>
            <TableCell>
              <HelpTooltip
                title={TOOLTIP_HELP_TITLE}
                text={TOOLTIP_HELP_CONTENT}
              >
                Renewal Matching Allowed
              </HelpTooltip>
            </TableCell>
            <TableCell>
              <HelpTooltip
                title={TOOLTIP_VISIBLE_HELP_TITLE}
                text={TOOLTIP_VISIBLE_HELP_CONTENT}
              >
                Renewals Visible
              </HelpTooltip>
            </TableCell>
          </TableRow>
        </TableHead>
        <EnhancedTableBody colSpan={3} isLoading={isLoading}>
          {selectedRows.map((row) => (
            <TableRow key={row.key}>
              <TableCell>
                {row.l2name} | {row.name}
              </TableCell>
              <TableCell>
                <StatusSwitch
                  l2Id={row.l2Id}
                  l3Id={row.l3Id}
                  updateData={setData}
                  defaultValue={row.renewalRenewalMatchingAllowed}
                  isDisabled={isReadOnly(row)}
                />
              </TableCell>
              <TableCell>
                <VisibilitySwitch
                  l2Id={row.l2Id}
                  l3Id={row.l3Id}
                  updateData={setData}
                  defaultValue={row.renewalRenewalFindingAllowed}
                  isDisabled={Boolean(row.renewalRenewalMatchingAllowed) || isReadOnly(row)}
                />
              </TableCell>
            </TableRow>
          ))}
        </EnhancedTableBody>
      </Table>
      <TablePagination />
    </>
  );
};
